import React from "react";
import { useLocation } from "react-router-dom";

//state
import { useRecoilState } from "recoil";
import docsAtom from "../core/atoms/docsAtom";
import countryAtom from "../core/atoms/countryAtom";
import wordsAtom from "../core/atoms/wordsAtom";

//components
import Header from "../components/Header/Header";
import Loader from "../components/Loader/Loader";

//utils
import get from "../core/utils/get";
import capitalize from "../core/utils/capitalize";

const normalizeDelimitedIds = (value: any, delimiter: string) => {
  if (value === null || typeof value === "undefined") return [];
  const str = String(value).trim();
  if (!str || str === "0") return [];
  return str
    .split(delimiter)
    .map((item) => String(item).trim())
    .filter((item) => item !== "" && item !== "0");
};

const normalizeTopics = (value: any) => {
  if (value === null || typeof value === "undefined") return [];
  const raw = String(value).trim();
  if (!raw || raw === "0") return [];
  if (raw.startsWith("^") && raw.endsWith("^") && raw.length > 1) {
    return normalizeDelimitedIds(raw.slice(1, -1), "^");
  }
  return normalizeDelimitedIds(raw, "^");
};

const requireValue = <T,>(value: T | undefined | null, message: string): T => {
  if (value === null || typeof value === "undefined") {
    throw new Error(message);
  }
  return value;
};

const Docs = () => {
  const { pathname } = useLocation();

  const mark = get("mark");

  const [docs] = useRecoilState(docsAtom);
  const [country] = useRecoilState(countryAtom);
  const [words] = useRecoilState(wordsAtom);

  const [loading, setLoading] = React.useState(true);

  const [cur, setCur] = React.useState<any>(null);
  const [parts, setParts] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (words?.site?.aware && words?.site?.tooriginal && words?.site?.body) {
      setLoading(false);
    }
  }, [words]);

  React.useEffect(() => {
    if (mark && parts && document.getElementById("id" + mark)) {
      // @ts-ignore
      document.getElementById("id" + mark).scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [parts]);

  React.useEffect(() => {
    const url = decodeURIComponent(pathname).replace("/doc/", "");
    // @ts-ignore
    if (docs && docs.docs && docs.docs.list) {
      // @ts-ignore
      const d = Object.entries(docs.docs.list).find((doc: any) => {
        return doc[1].doc_alias === url;
      });
      setCur(d ? d[1] : {});
    }
  }, [docs]);

  React.useEffect(() => {
    // @ts-ignore
    if (
      docs &&
      docs.recommendations &&
      docs.recommendations.list &&
      cur?.doc_id
    ) {
      // @ts-ignore
      const arr = Object.entries(docs.recommendations.list)
        .filter((part: any) => {
          if (+part[1].doc_id === +cur.doc_id) {
            return part[1];
          }
        })
        .map((i) => {
          const d: any = i[1];
          return {
            num: +d.part_id,
            doc: +d.doc_id,
            recommendation: d.part_text,
            body: +d.body_id,
            theme: normalizeTopics(d.part_topics),
            followUp: { color: d.part_follow, text: d.part_follow_text },
            country: normalizeDelimitedIds(d.part_countries, ","),
            status: d.part_approved,
            comment: d.part_comment,
            link: d.part_links,
          };
        });
      setParts(arr);
    }
  }, [cur]);

  if (!cur || !parts || loading) return <Loader active={!cur} />;

  const siteAwareText =
    words?.site?.aware ||
    "Be aware, you are looking at a truncated version of the document. Please follow the link to see the original publication:";

  const siteToOriginalLabel = words?.site?.tooriginal || "Link";
  const siteToOriginalLink = cur.doc_link || "#";

  return (
    <div>
      <Header />
      <div className="section_content center">
        <div className="row c12">
          <div className="wrapper c5">
            <div id="document-info" className="c5">
              <h2>{cur.doc_name}</h2>
              <p>{cur.doc_description}</p>
              <p>
                {siteAwareText}&nbsp;
                <a
                  href={siteToOriginalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {siteToOriginalLabel}
                </a>
              </p>
            </div>
          </div>
          <div className="text c7">
            {parts
              ? parts.map((part) => {
                  return mark && +part.num === +mark ? (
                    <div key={`part${part.num}`}>
                      <p
                        id={`id${part.num}`}
                        className="paragraph_recommendation marked"
                      >
                        {part.recommendation}
                      </p>
                      <div className="recommendation_details">
                        {part.theme.length > 0 ? (
                          <div className="recommendation_details_themes">
                            {part.theme.map((theme: any) => {
                              const topic = requireValue(
                                docs?.topics?.list?.[+theme],
                                `Missing topic ${theme} for recommendation ${part.num}`
                              );
                              return (
                                <div className="theme" key={"topic" + theme}>
                                  {topic.topic_name}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                        <div className="recommendation_details_row">
                          <div className="name">
                            {capitalize(words.site.body)}:
                          </div>
                          <div className="value">
                            {requireValue(
                              docs?.bodies?.list?.[+part.body],
                              `Missing body ${part.body} for recommendation ${part.num}`
                            ).body_name}
                            {/*{*/}
                            {/*    docs.bodies.list[+part.body].body_description ?*/}
                            {/*        <>&nbsp;<sup data-description={docs.bodies.list[+part.body].body_description}*/}
                            {/*                     className="mechanism-desctiption">*/}
                            {/*            ?*/}
                            {/*        </sup></> : null*/}
                            {/*}*/}
                          </div>
                        </div>
                        {part.followUp && part.followUp.text ? (
                          <div className="recommendation_details_row">
                            <div className="name">
                              {capitalize(words.site.followup)}:
                            </div>
                            <div className="value">
                              {part.followUp.color ? (
                                <>
                                  {part.followUp.color}
                                  <br />
                                </>
                              ) : (
                                ""
                              )}
                              {part.followUp.text}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p
                      id={`id${part.num}`}
                      key={`part${part.num}`}
                      className="paragraph_recommendation"
                    >
                      {part.recommendation}
                    </p>
                  );
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Docs;
